import { TanStackDevtools } from "@tanstack/react-devtools";
import {
	createRootRoute,
	HeadContent,
	Link,
	redirect,
	Scripts,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { useEffect } from "react";
import { AnalyticsProvider, RootShell } from "@/components/blocks/RootShell";
import { Button } from "@/components/ui/button";
import { usePageViewAnalytics } from "@/hooks/use-page-view-analytics";
import {
	addLocaleToPathname,
	buildPath,
	DEFAULT_LOCALE,
	extractLocaleFromPathname,
	getClientLocaleFromCookie,
	getSafeLocale,
	readServerLocale,
	setLocaleCookie,
	stripLocaleFromPathname,
} from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";

import appCss from "../styles.css?url";

export const Route = createRootRoute({
	beforeLoad: ({ location, serverContext }) => {
		const { locale: pathLocale, segment } = extractLocaleFromPathname(
			location.pathname,
		);
		const clientCookieLocale = getClientLocaleFromCookie();
		const serverLocale = readServerLocale(serverContext);

		const resolvedLocale = getSafeLocale(
			pathLocale ?? undefined,
			serverLocale,
			clientCookieLocale,
		);
		const basePathname = segment
			? stripLocaleFromPathname(location.pathname)
			: location.pathname;
		const isPublicResourcePath =
			basePathname === "/sitemap.xml" ||
			basePathname === "/robots.txt" ||
			basePathname === "/llms.txt" ||
			basePathname === "/llms-full.txt" ||
			basePathname.startsWith("/content/");

		if (isPublicResourcePath) {
			if (segment) {
				throw redirect({
					href: buildPath(basePathname, location.searchStr, location.hash),
				});
			}
			return {
				locale: resolvedLocale,
				isLocalePrefixed: false,
			};
		}
		const shouldPrefix = resolvedLocale !== DEFAULT_LOCALE;
		const targetPathname = shouldPrefix
			? addLocaleToPathname(basePathname, resolvedLocale)
			: basePathname;

		if (targetPathname !== location.pathname) {
			throw redirect({
				href: buildPath(targetPathname, location.searchStr, location.hash),
			});
		}

		return {
			locale: resolvedLocale,
			isLocalePrefixed: shouldPrefix,
		};
	},
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				name: "theme-color",
				content: "#111315",
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/favicon.svg",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32.png",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
		],
	}),

	shellComponent: RootDocument,
	notFoundComponent: NotFound,
});

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale } = Route.useRouteContext();

	useEffect(() => {
		setLocaleCookie(locale);
		if (typeof document !== "undefined") {
			document.documentElement.lang = locale;
		}
	}, [locale]);

	return (
		<html lang={locale}>
			<head>
				<HeadContent />
			</head>
			<body>
				<AnalyticsProvider>
					<PageViewAnalytics locale={locale} />
					<RootShell>{children}</RootShell>
				</AnalyticsProvider>
				{import.meta.env.DEV ? (
					<TanStackDevtools
						config={{ position: "bottom-right" }}
						plugins={[
							{
								name: "TanStack Router",
								render: <TanStackRouterDevtoolsPanel />,
							},
						]}
					/>
				) : null}
				<Scripts />
			</body>
		</html>
	);
}

function PageViewAnalytics({ locale }: { locale: string }) {
	usePageViewAnalytics(locale);
	return null;
}

function NotFound() {
	const { t, localeParam } = useI18n();

	return (
		<div className="flex min-h-[calc(100vh-12rem)] flex-col items-center justify-center px-6">
			<div className="max-w-md space-y-4 text-center">
				<p className="text-sm font-medium text-muted-foreground">
					{t((t) => t.notFound.label)}
				</p>
				<h1 className="text-6xl font-bold tracking-tight">404</h1>
				<p className="text-2xl font-semibold">{t((t) => t.notFound.title)}</p>
				<p className="text-muted-foreground">
					{t((t) => t.notFound.description)}
				</p>
				<Button asChild>
					<Link to="/{-$locale}" params={{ locale: localeParam }}>
						{t((t) => t.notFound.backHome)}
					</Link>
				</Button>
			</div>
		</div>
	);
}
